import React, { useState } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconDetailsSplit24, IconPlus16 } from "./TicketV2Icons";
import {
  inter,
  sxMetaLabel,
  sxMetaDateValue,
  tv2EmptyPlaceholderSx,
  tv2LinkActionSx,
  tv2UserDataCardSx,
} from "./ticketDetails/styles";
import { TicketDetailsSection } from "./ticketDetails/TicketDetailsSection";
import {
  StatusSelect,
  PrioritySelect,
  Field,
} from "./ticketDetails/TicketDetailsSelects";
import TeamMemberSelect from "../TicketSectionComponents/TicketMainSection/TeamMemberSelect";

/**
 * Figma empty Details panel used while Create Ticket is open,
 * and as the visual reference for empty requester / group / history slots.
 */
function TicketDetailsEmpty({ onToggleDetails }) {
  const [openSections, setOpenSections] = useState({
    properties: true,
    user: true,
    history: true,
    transcript: true,
    apps: false,
  });

  const toggle = (key) => {
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <Box
      component="aside"
      sx={{
        width: 280,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: inter,
        boxSizing: "border-box",
        bgcolor: "#FFF",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: TV2_COLUMN_HEADER_HEIGHT,
          minHeight: TV2_COLUMN_HEADER_HEIGHT,
          boxSizing: "border-box",
          flexShrink: 0,
          px: 2,
          borderBottom: "1px solid #E5E9EC",
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#061522", fontFamily: inter }}>
          Details
        </Typography>
        <IconButton
          size="small"
          sx={{ p: 0.5, color: "#405261" }}
          aria-label="Hide details panel"
          onClick={onToggleDetails}
        >
          <IconDetailsSplit24 />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 2, py: 2 }}>
        <TicketDetailsSection
          title="Ticket Properties"
          open={openSections.properties}
          onToggle={() => toggle("properties")}
        >
          <Field label="Status">
            <StatusSelect value="" onChange={() => {}} placeholder="Select Option" />
          </Field>
          <Field label="Priority">
            <PrioritySelect value="" onChange={() => {}} placeholder="Select Option" />
          </Field>
          <Field label="Assignee">
            <TeamMemberSelect
              value=""
              onChange={() => {}}
              teamMembers={[]}
              placeholder="Select Option"
              dropdownWidth="248px"
              wrapperStyle={{ width: "248px", minWidth: "248px", display: "block" }}
              triggerStyles={{ height: "40px" }}
              dropDownTop="calc(100% + 8px)"
              showMemberEmailInDropdown={false}
            />
          </Field>

          <Stack gap={1}>
            <Typography sx={sxMetaLabel}>Tags</Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "12px",
                border: "1px solid #E5E9EC",
                background: "#FFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconPlus16 />
            </Box>
          </Stack>

          <Stack gap={0.5}>
            <Typography sx={sxMetaLabel}>Source</Typography>
            <Typography sx={{ ...sxMetaDateValue, fontWeight: 400 }}>—</Typography>
          </Stack>
        </TicketDetailsSection>

        <TicketDetailsSection
          title="User Data"
          open={openSections.user}
          onToggle={() => toggle("user")}
        >
          <Stack gap={2} sx={{ width: 248, maxWidth: "100%" }}>
            <Stack gap={1.25} sx={tv2UserDataCardSx}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  sx={{
                    color: "#061522",
                    fontFamily: inter,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "18px",
                  }}
                >
                  Requester
                </Typography>
                <Typography component="span" sx={tv2LinkActionSx}>
                  Add Requester
                </Typography>
              </Stack>
              <Box sx={tv2EmptyPlaceholderSx}>No Requester</Box>
              <Stack direction="row" alignItems="center" gap={1.25} sx={{ mt: 0.25 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "16px",
                    border: "1px solid #E5E9EC",
                    background: "#FFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    flexShrink: 0,
                  }}
                >
                  <IconPlus16 />
                </Box>
                <Typography sx={tv2LinkActionSx}>Add more people</Typography>
              </Stack>
            </Stack>

            <Stack gap={1.25} sx={tv2UserDataCardSx}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  sx={{
                    color: "#061522",
                    fontFamily: inter,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "18px",
                  }}
                >
                  Groups
                </Typography>
                <Typography component="span" sx={tv2LinkActionSx}>
                  Add Group
                </Typography>
              </Stack>
              <Box sx={tv2EmptyPlaceholderSx}>No Group Selected</Box>
            </Stack>
          </Stack>
        </TicketDetailsSection>

        <TicketDetailsSection
          title="History"
          open={openSections.history}
          onToggle={() => toggle("history")}
        >
          <Box sx={tv2EmptyPlaceholderSx}>No History</Box>
        </TicketDetailsSection>

        <TicketDetailsSection
          title="Transcript"
          open={openSections.transcript}
          onToggle={() => toggle("transcript")}
        >
          <Box sx={tv2EmptyPlaceholderSx}>No transcript available</Box>
        </TicketDetailsSection>

        <TicketDetailsSection
          title="Apps"
          open={openSections.apps}
          onToggle={() => toggle("apps")}
        >
          <Typography sx={{ fontSize: 12, color: "#8B97A2", fontFamily: inter }}>
            No apps connected.
          </Typography>
        </TicketDetailsSection>
      </Box>
    </Box>
  );
}

export default TicketDetailsEmpty;
